/**
 * Tenants (/tenants): every Microsoft 365 tenant Guardian monitors, on
 * mockup carbon/01's brief and carbon/02's table.
 *
 *   1. The brief (BriefHero): an action-first h1 naming the tenant to scan
 *      next (never scanned first, then any not scanned in the last day), its
 *      lede, and the view's one primary button, which starts that scan.
 *   2. The Tenants DataTable: search and Refresh in the toolbar; per row the
 *      tenant, its state, its last scan, "Scan now" with the day's remaining
 *      scans, and the three dialogs: Tenant settings, Monitored resources and
 *      Refresh baseline.
 *
 * The breadcrumb names the page; the h1 is the verdict, including on the
 * empty and failed states.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'
import TenantSettingsModal from '@/components/TenantSettingsModal'
import BaselineRefreshDialog from '@/components/BaselineRefreshDialog'
import MonitoredResourcesModal from '@/components/MonitoredResourcesModal'
import EmptyState from '@/components/shared/EmptyState'
import { useTenantContext } from '@/hooks/useTenantContext'
import type { ActionBrief } from '@/lib/brief-shape'
import type { Tenant } from '@/types'
import { BriefHero } from '@/components/carbon/BriefHero'
import { Button } from '@/components/carbon/Button'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { IconArrowRight } from '@/components/carbon/icons'
import { Spinner } from '@/components/carbon/Spinner'
import { TableSearch } from '@/components/carbon/TableSearch'
import { formatClock } from '@/components/AdminPortal/admin-format'
import { composeScanLeadBrief, lastAssessedDate } from '@/components/AdminPortal/page-briefs'

const LOAD_FAILED_BRIEF: ActionBrief = {
  headline: 'Reload the page to see your tenants.',
  lede: '**The tenant list did not load**, so Guardian cannot show when each tenant was scanned.',
}

export default function Tenants() {
  const [searchQuery, setSearchQuery] = useState('')
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<any>(null)
  const [baselineRefreshDialogOpen, setBaselineRefreshDialogOpen] = useState(false)
  const [selectedTenantForBaseline, setSelectedTenantForBaseline] = useState<any>(null)
  const [monitoredResourcesModalOpen, setMonitoredResourcesModalOpen] = useState(false)
  const [selectedTenantForResources, setSelectedTenantForResources] = useState<any>(null)
  const queryClient = useQueryClient()
  // /admin is behind GuardianAdminRoute; only offer the Organizations link to
  // users who can actually reach it. Everyone else is told whom to ask.
  const { isGlobalAdmin } = useTenantContext()

  const {
    data: tenants = [],
    isLoading,
    isFetching,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['tenants'],
    queryFn: api.getTenants,
  })

  const [activeScanJobs, setActiveScanJobs] = useState<Map<string, string>>(new Map())

  const scanMutation = useMutation({
    mutationFn: (tenantId: string) => api.scanTenant(tenantId),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      queryClient.invalidateQueries({ queryKey: ['scanLimits'] })

      // Check the status field in the response to determine success/failure
      if (data && data.status === 'Failed') {
        toast.error(data.errorMessage || 'Scan failed.')
      } else if (data && data.status === 'RateLimited') {
        toast.error(data.errorMessage || "Today's scan limit is reached. Try again tomorrow.")
      } else if (data && data.status === 'Queued' && data.jobId) {
        // New async response - store the job ID and start polling
        setActiveScanJobs(prev => new Map(prev.set(data.tenantId, data.jobId)))
        toast.success('Scan started. Guardian is tracking progress.')
        startJobPolling(data.jobId, data.tenantId)
      } else {
        // Legacy response format
        toast.success('Scan started.')
      }
    },
    onError: (error: any) => {
      console.error('Scan mutation error:', error)
      if (error.response?.status === 429) {
        toast.error("Today's scan limit is reached. Try again tomorrow.")
      } else if (error.response?.status === 409) {
        const message = error.response?.data?.message || 'A scan or baseline refresh is already running for this tenant. Wait for it to finish.'
        toast.error(message, { duration: 6000 })
      } else if (error.response?.status >= 400) {
        toast.error(error.response?.data?.message || error.response?.data?.errorMessage || 'Scan could not start. Try again.')
      }
    },
  })

  const startJobPolling = (jobId: string, tenantId: string) => {
    const pollJob = async () => {
      try {
        const jobStatus = await api.getScanJobStatus(jobId)

        if (jobStatus.status === 'Completed') {
          setActiveScanJobs(prev => {
            const newMap = new Map(prev)
            newMap.delete(tenantId)
            return newMap
          })
          toast.success('Scan completed.')
          queryClient.invalidateQueries({ queryKey: ['tenants'] })
          queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        } else if (jobStatus.status === 'Failed') {
          setActiveScanJobs(prev => {
            const newMap = new Map(prev)
            newMap.delete(tenantId)
            return newMap
          })
          toast.error(`Scan failed: ${jobStatus.errorMessage || 'Unknown error'}`)
        } else if (jobStatus.status === 'Running' || jobStatus.status === 'Queued') {
          // Continue polling
          setTimeout(pollJob, 3000) // Poll every 3 seconds
        }
      } catch (error) {
        console.error('Error polling job status:', error)
        setActiveScanJobs(prev => {
          const newMap = new Map(prev)
          newMap.delete(tenantId)
          return newMap
        })
      }
    }

    // Start polling after a short delay
    setTimeout(pollJob, 2000)
  }

  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.domain?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleOpenSettings = (tenant: any) => {
    setSelectedTenant(tenant)
    setSettingsModalOpen(true)
  }

  const handleCloseSettings = () => {
    setSettingsModalOpen(false)
    setSelectedTenant(null)
  }

  const handleOpenBaselineRefresh = (tenant: any) => {
    setSelectedTenantForBaseline(tenant)
    setBaselineRefreshDialogOpen(true)
  }

  const handleCloseBaselineRefresh = () => {
    setBaselineRefreshDialogOpen(false)
    setSelectedTenantForBaseline(null)
  }

  const handleBaselineRefreshSuccess = () => {
    // Refresh tenants data after baseline refresh
    refetch()
  }

  const handleOpenMonitoredResources = (tenant: any) => {
    setSelectedTenantForResources(tenant)
    setMonitoredResourcesModalOpen(true)
  }

  const handleCloseMonitoredResources = () => {
    setMonitoredResourcesModalOpen(false)
    setSelectedTenantForResources(null)
  }

  const handleMonitoredResourcesSuccess = () => {
    // Refresh tenants data after resource changes
    refetch()
  }

  // Custom hook to get scan limits for a tenant
  const useScanLimits = (tenantId: string) => {
    return useQuery({
      queryKey: ['scanLimits', tenantId],
      queryFn: () => api.getScanLimits(tenantId),
      enabled: !!tenantId,
      staleTime: 30000, // 30 seconds
    })
  }

  // ScanButton component with limits display and job status
  const ScanButton = ({ tenant }: { tenant: any }) => {
    const { data: scanLimits } = useScanLimits(tenant.tenantId)

    const canScan = scanLimits?.canScan !== false && tenant.isActive
    const remainingScans = scanLimits?.remainingScans ?? 0
    const isCreatingJob = scanMutation.isPending
    const activeJobId = activeScanJobs.get(tenant.tenantId)
    const hasActiveJob = !!activeJobId

    return (
      <div className="flex min-w-0 flex-col items-start gap-0.5">
        <Button
          kind="ghost"
          size="sm"
          onClick={() => scanMutation.mutate(tenant.tenantId)}
          disabled={isCreatingJob || hasActiveJob || !canScan}
          aria-label={`Scan ${tenant.name} now`}
          title={
            hasActiveJob
              ? 'Scan in progress'
              : isCreatingJob
              ? 'Starting scan'
              : scanLimits?.reasonBlocked || (canScan ? 'Run a configuration scan' : 'Scan unavailable')
          }
          icon={hasActiveJob ? <Spinner /> : undefined}
          wrap={false}
          className="-ml-3"
        >
          {isCreatingJob ? 'Starting scan' : hasActiveJob ? 'Scanning' : 'Scan now'}
        </Button>

        {/* Show remaining scans if limits are enforced */}
        {scanLimits && scanLimits.dailyLimit < 1000 && !hasActiveJob && (
          <span className="text-helper text-text-helper">
            {remainingScans} of {scanLimits.dailyLimit} scans left today
          </span>
        )}
      </div>
    )
  }

  // ── The brief: the tenant to scan next, among the active ones ────────────
  const activeTenants = tenants.filter(t => t.isActive)
  const { brief, lead } = composeScanLeadBrief(activeTenants, new Date(), tenants.length - activeTenants.length)
  const { data: leadLimits } = useScanLimits(lead?.tenantId ?? '')
  const leadBusy = !!lead && (scanMutation.isPending || activeScanJobs.has(lead.tenantId))
  const leadCanScan = !!lead && leadLimits?.canScan !== false
  const leadNeverScanned = !!lead && lastAssessedDate(lead) === null

  const primary = lead ? (
    <Button
      kind="primary"
      icon={leadBusy ? <Spinner /> : <IconArrowRight />}
      onClick={() => scanMutation.mutate(lead.tenantId)}
      disabled={leadBusy || !leadCanScan}
      title={leadLimits?.reasonBlocked || undefined}
    >
      {leadNeverScanned ? `Run the first scan on ${lead.name}` : `Scan ${lead.name}`}
    </Button>
  ) : undefined

  const provenance =
    !error && !isLoading && dataUpdatedAt > 0
      ? `Listed by Guardian at ${formatClock(new Date(dataUpdatedAt))} · ${tenants.length} ${tenants.length === 1 ? 'tenant' : 'tenants'}`
      : undefined

  const columns: DataColumn<Tenant>[] = [
    {
      id: 'tenant',
      header: 'Tenant',
      primary: true,
      width: '28%',
      cell: tenant => <TableItem title={tenant.name} sub={tenant.domain} />,
    },
    {
      id: 'state',
      header: 'State',
      inlineOnPhone: true,
      cell: tenant => (tenant.isActive ? 'Active' : <span className="text-text-helper">Inactive</span>),
    },
    {
      id: 'lastScan',
      header: 'Last scan',
      inlineOnPhone: true,
      cell: tenant => {
        // The creation stamp the backend writes into lastScanAt is not a scan.
        const at = lastAssessedDate(tenant)
        return at ? formatDistanceToNow(at, { addSuffix: true }) : <span className="text-text-helper">Not scanned yet</span>
      },
    },
    { id: 'scan', header: 'Scan', cell: tenant => <ScanButton tenant={tenant} /> },
    {
      id: 'actions',
      header: <span className="sr-only">Tenant actions</span>,
      cell: tenant => (
        <div className="-ml-3 flex flex-wrap items-center gap-x-1">
          <Button
            kind="ghost"
            size="sm"
            wrap={false}
            onClick={() => handleOpenSettings(tenant)}
            aria-label={`Tenant settings for ${tenant.name}`}
          >
            Settings
          </Button>
          <Button
            kind="ghost"
            size="sm"
            wrap={false}
            onClick={() => handleOpenMonitoredResources(tenant)}
            aria-label={`Monitored resources for ${tenant.name}`}
            title="Configure which M365 resources to monitor for this tenant"
          >
            Monitored resources
          </Button>
          <Button
            kind="ghost"
            size="sm"
            wrap={false}
            onClick={() => handleOpenBaselineRefresh(tenant)}
            aria-label={`Refresh baseline for ${tenant.name}`}
            title="Refresh baseline from current M365 configuration"
          >
            Refresh baseline
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div>
      <section aria-labelledby="tenants-verdict" className="mt-8">
        <BriefHero
          brief={error ? LOAD_FAILED_BRIEF : brief}
          size="pillar"
          headingId="tenants-verdict"
          isLoading={isLoading}
          provenance={provenance}
          secondaryAction={
            error ? (
              <Button kind="tertiary" onClick={() => refetch()}>
                Retry
              </Button>
            ) : (
              primary
            )
          }
        />
      </section>

      {!error && (
        <DataTable
          className="mt-12"
          title="Tenants"
          description="The Microsoft 365 tenants Guardian monitors, and when each was last scanned."
          columns={columns}
          rows={filteredTenants}
          rowId={tenant => tenant.tenantId}
          rowLabel={tenant => tenant.name}
          isLoading={isLoading}
          toolbar={
            <>
              <TableSearch value={searchQuery} onChange={setSearchQuery} label="Search tenants" />
              <Button kind="ghost" onClick={() => refetch()} disabled={isFetching} icon={isFetching ? <Spinner /> : undefined} className="flex-none">
                Refresh
              </Button>
            </>
          }
          empty={
            searchQuery ? (
              <EmptyState
                align="center"
                headline={`No tenants match "${searchQuery}".`}
                body="Clear the search to see every tenant."
              />
            ) : (
              <EmptyState
                align="center"
                headline="No tenant is monitored yet."
                body={
                  isGlobalAdmin
                    ? 'Add the tenant in Organizations, then run its first scan from this page.'
                    : 'Ask a Guardian admin to add the tenant in Organizations and run its first scan.'
                }
                cta={isGlobalAdmin ? { label: 'Open organizations', to: '/admin?tab=organizations' } : undefined}
              />
            )
          }
        />
      )}

      {/* Settings Modal */}
      {selectedTenant && (
        <TenantSettingsModal
          isOpen={settingsModalOpen}
          onClose={handleCloseSettings}
          tenant={selectedTenant}
        />
      )}

      {/* Baseline Refresh Dialog */}
      {selectedTenantForBaseline && (
        <BaselineRefreshDialog
          isOpen={baselineRefreshDialogOpen}
          onClose={handleCloseBaselineRefresh}
          tenantId={selectedTenantForBaseline.tenantId}
          onSuccess={handleBaselineRefreshSuccess}
        />
      )}

      {/* Monitored Resources Modal */}
      {selectedTenantForResources && (
        <MonitoredResourcesModal
          isOpen={monitoredResourcesModalOpen}
          onClose={handleCloseMonitoredResources}
          tenant={selectedTenantForResources}
          onSuccess={handleMonitoredResourcesSuccess}
        />
      )}
    </div>
  )
}
